import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export const CONSENT_CATALOG = {
  gmail_read: {
    title: 'Look in Gmail for recoveries',
    allows: [
      'Search recent mail for refunds, renewals, bills, receipts, and delivery updates',
      'Open those threads so we can quote dates and amounts',
    ],
    forbids: [
      'Send any email until you approve the exact draft',
      'Use your mail to train a public model',
      'Read unrelated personal mail on purpose',
    ],
    affirm: 'I allow BilledTwice to read recovery-related Gmail for me.',
    action: 'Allow Gmail reading',
  },
  gmail_send: {
    title: 'Send approved recovery emails from Gmail',
    allows: [
      'Send the one draft you just approved, to the address shown',
    ],
    forbids: [
      'Send anything you have not approved',
      'Change the recipient, amount, or wording after you approve',
    ],
    affirm: 'I allow sending from Gmail only after I tap Approve & Send.',
    action: 'Allow Gmail send',
  },
  bank_match: {
    title: 'Connect your bank account to verify savings',
    allows: [
      'Detect recurring subscriptions',
      'Identify duplicate charges',
      'Confirm refunds and merchant credits',
      'Verify canceled subscriptions stop charging',
      'Track bill increases over time',
    ],
    forbids: [
      'Move money or make payments',
      'Initiate transfers or file disputes',
      'Make credit decisions or give financial advice',
    ],
    affirm: 'I allow BilledTwice to read transaction activity I authorize. You do not move money.',
    action: 'Allow bank matching',
  },
  documents: {
    title: 'Read files you give us',
    allows: [
      'Read emails, receipts, and PDFs you paste or upload',
      'Use them only to find and draft recoveries for you',
    ],
    forbids: [
      'Sell that file',
      'Send as you without your approval',
      'Use it to train a public model',
    ],
    affirm: 'I allow BilledTwice to read files I paste or upload.',
    action: 'Allow file reading',
  },
};

export function consentActive(consents, kind) {
  return Boolean((consents?.active || []).includes(kind));
}

export default function ConsentGate({
  kind,
  consents,
  busy,
  onAllow,
  actionLabel,
  children,
  blocked,
}) {
  const spec = CONSENT_CATALOG[kind];
  const [checked, setChecked] = useState(false);
  const [hint, setHint] = useState('');
  if (!spec) return children || null;
  if (consents === null) {
    return <p className="np-muted">Checking what you already allowed…</p>;
  }
  if (consentActive(consents, kind)) {
    return children || null;
  }

  const locked = Boolean(blocked);
  const label = actionLabel || spec.action;

  function submit() {
    if (busy) return;
    if (locked) {
      setHint(blocked);
      return;
    }
    if (!checked) {
      setHint('Check the box above first. That turns the button on.');
      return;
    }
    setHint('');
    onAllow(kind);
  }

  return (
    <div className="np-consent">
      <h3>{spec.title}</h3>
      <p>We will:</p>
      <ul className="np-list">
        {spec.allows.map((item) => <li key={item}>{item}</li>)}
      </ul>
      <p style={{ marginTop: 12 }}>We will not:</p>
      <ul className="np-list">
        {spec.forbids.map((item) => <li key={item}>{item}</li>)}
      </ul>
      {locked ? null : (
        <label className="np-check">
          <input
            type="checkbox"
            checked={checked}
            disabled={busy}
            onChange={(event) => {
              setChecked(event.target.checked);
              if (event.target.checked) setHint('');
            }}
          />
          {spec.affirm}
        </label>
      )}
      <p className="np-muted" style={{ marginTop: 8 }}>
        <Link to="/privacy">Privacy</Link>
        {' · '}
        <Link to="/terms">Terms</Link>
        {' · You can turn this off later in Settings.'}
      </p>
      {locked ? (
        <p className="np-error" style={{ marginTop: 12 }}>{blocked}</p>
      ) : (
        <>
          <p className="np-muted" style={{ marginTop: 8 }}>
            {checked ? `Ready — click ${label}.` : `Check the box, then click ${label}.`}
          </p>
          {hint ? <p className="np-error" style={{ marginTop: 8 }}>{hint}</p> : null}
          <div className="np-actions">
            <button
              type="button"
              className={checked ? 'np-btn' : 'np-btn ghost'}
              disabled={busy}
              onClick={submit}
            >
              {label}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
